<template>
  <div class="checkout-summary">
    <div class="checkout-summary__container">
      <div
        class="checkout-summary__header"
        :class="isMobileItemsShown ? 'mobile-show' : 'mobile-hide'"
      >
        <h4 class="checkout-summary__header-title">
          {{ $t('landingpage.checkout-page.summary.your-order') }}
        </h4>

        <CountdownTimer
          ref="countdownTimerRef"
          :time-end="orderDetails?.expires_at"
          @time-expired="emit('countdown-timer-expired')"
        />
      </div>

      <template
        v-if="
          (isTicketPurchaseMode && !groupedTickets.length) ||
          (!isTicketPurchaseMode &&
            !mainGuestTickets.length &&
            !mainGuestAddOns.length &&
            !mainGuest?.contact?.first_name)
        "
      >
        <div
          class="checkout-summary__item checkout-summary__item-no-tickets"
          :class="isMobileItemsShown ? 'mobile-show' : 'mobile-hide'"
        >
          <div class="checkout-summary__item-no-tickets-icon">
            <FontAwesomeIcon :icon="['far', 'cart-shopping']" />
          </div>
          <p>
            {{
              !tickets.length
                ? $t('landingpage.checkout-page.add-ons.no-items-selected-yet')
                : $t(
                    'landingpage.checkout-page.add-ons.no-tickets-selected-yet',
                  )
            }}
          </p>
        </div>
      </template>

      <template v-else>
        <div
          class="checkout-summary__item"
          :class="isMobileItemsShown ? 'mobile-show' : 'mobile-hide'"
        >
          <!-- Ticket Purchase Mode: Show Grouped Tickets -->
          <template v-if="isTicketPurchaseMode">
            <div class="checkout-summary__item-row">
              <div class="checkout-summary__item-content w-full">
                <p class="checkout-summary__item-title mb-2">
                  {{
                    $t(
                      'landingpage.checkout-page.summary.ticket-purchase.tickets',
                    )
                  }}
                </p>

                <!-- Grouped Tickets -->
                <div
                  v-for="(ticketGroup, index) in groupedTickets"
                  :key="index"
                  class="checkout-summary__item-price-row"
                >
                  <p class="checkout-summary__item-label">
                    {{ ticketGroup.quantity }}x {{ ticketGroup.name }}
                    <template
                      v-if="
                        ticketGroup.quantity > 1 && ticketGroup.unitPrice > 0
                      "
                    >
                      <span class="text-[10px] opacity-70">
                        &agrave;
                        {{
                          formatCurrencyLeft(
                            ticketGroup.unitPrice,
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            },
                            orderDetails?.currency,
                          )
                        }}
                      </span>
                    </template>
                  </p>

                  <p
                    v-if="ticketGroup.unitPrice > 0"
                    class="checkout-summary__item-price"
                  >
                    {{
                      formatCurrencyLeft(
                        ticketGroup.totalPrice,
                        { minimumFractionDigits: 2, maximumFractionDigits: 2 },
                        orderDetails?.currency,
                      )
                    }}
                  </p>
                </div>

                <!-- Guest Manager Info (if billing details entered) -->
                <div
                  v-if="mainGuest?.contact.first_name"
                  class="checkout-summary__buyer-info mt-4 pt-4 border-t border-gray-200"
                >
                  <p class="checkout-summary__item-title text-sm">
                    {{
                      $t(
                        'landingpage.checkout-page.summary.ticket-purchase.buyer',
                      )
                    }}
                  </p>
                  <p class="checkout-summary__item-subtitle text-sm">
                    {{ mainGuest?.contact.first_name ?? '' }}
                    {{ mainGuest?.contact.last_name ?? '' }}
                  </p>
                </div>
              </div>
            </div>
          </template>

          <!-- Registration Mode: Show Main Guest and Companions -->
          <template v-else>
            <!-- Main Guest -->
            <div class="checkout-summary__item-row">
              <div class="checkout-summary__item-number">1</div>
              <div class="checkout-summary__item-content">
                <p class="checkout-summary__item-title">
                  {{ $t('landingpage.checkout-page.confirmation.main-guest') }}
                </p>
                <p
                  v-if="mainGuest?.contact.first_name"
                  class="checkout-summary__item-subtitle"
                >
                  {{ mainGuest?.contact.first_name ?? '' }}
                  {{ mainGuest?.contact.last_name ?? '' }}
                </p>

                <!-- Main Guest Ticket -->
                <div
                  v-for="item in mainGuestTickets"
                  :key="item.id"
                  class="checkout-summary__item-price-row"
                >
                  <p class="checkout-summary__item-label">
                    {{ item?.product?.name[activeLocale] }}
                  </p>
                  <p
                    v-if="getTicketProductPrice(item)?.price > 0"
                    class="checkout-summary__item-price"
                  >
                    {{ getTicketProductPrice(item)?.formatted }}
                  </p>
                </div>

                <!-- Main Guest Add Ons -->
                <template
                  v-for="(group, index) in groupedMainGuestAddOns"
                  :key="`${group.product.id}-${index}`"
                >
                  <div class="checkout-summary__item-price-row add-on">
                    <p class="checkout-summary__item-label">
                      +
                      {{
                        getAvailabilityType(group.items[0]) === 'fixed'
                          ? group.items[0].quantity > 1
                            ? `${group.items[0].quantity}x`
                            : ''
                          : group.items.length > 1
                            ? `${group.items.length}x`
                            : ''
                      }}
                      {{ group.product?.name[activeLocale] }}
                    </p>
                    <p
                      v-if="
                        !isFree &&
                        getAvailabilityType(group.items[0]) === 'fixed'
                      "
                      class="checkout-summary__item-price"
                    >
                      {{ getAddOnProductPrice(group.items[0]) }}
                    </p>
                  </div>

                  <template
                    v-if="getAvailabilityType(group.items[0]) === 'flexible'"
                  >
                    <div
                      v-for="dateGroup in groupItemsByDate(group.items)"
                      :key="dateGroup.date"
                      class="add-on-date"
                    >
                      <p class="text-[10px]! font-medium">
                        {{ dateGroup.date }}
                      </p>
                      <div
                        v-for="item in dateGroup.slots"
                        :key="item.id"
                        class="flex justify-between"
                      >
                        <p class="text-[10px]!">
                          {{
                            `${formatTime(item?.starts_at, eventSettings?.timezone, false)} - ${formatTime(item?.ends_at, eventSettings?.timezone, false)} (${Math.round((new Date(item?.ends_at) - new Date(item?.starts_at)) / 60000)} min)`
                          }}
                        </p>
                        <p class="text-[10px]!">
                          {{ getAddOnProductPrice(item) }}
                        </p>
                      </div>
                    </div>
                  </template>

                  <div
                    v-else-if="getAvailabilityType(group.items[0]) === 'nights'"
                    class="add-on-date"
                  >
                    <template
                      v-for="range in [getNightsDateRange(group.items)]"
                      :key="range"
                    >
                      <p class="text-[10px]!">
                        {{ formatDateWithWeekday(range.first?.starts_at) }}
                        -
                        {{ formatDateWithWeekday(range.last?.ends_at) }}
                        ({{ group.items.length }}
                        {{
                          group.items.length === 1
                            ? $t('common.night')
                            : $t('common.nights')
                        }})
                      </p>
                    </template>

                    <p
                      v-for="(breakdown, idx) in nightsCalculationBreakdown(
                        group.items,
                      )"
                      :key="idx"
                      class="flex gap-2 justify-between mt-1"
                    >
                      <span>
                        {{ breakdown.count }}x
                        {{
                          formatCurrencyLeft(
                            breakdown.grossPrice,
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            },
                            orderDetails?.currency,
                          )
                        }}
                      </span>
                      <span>
                        {{
                          formatCurrencyLeft(
                            breakdown.count * breakdown.grossPrice,
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            },
                            orderDetails?.currency,
                          )
                        }}
                      </span>
                    </p>
                  </div>
                </template>
              </div>
            </div>

            <!-- Companions -->
            <template v-if="isCompanionsEnabled">
              <div
                v-for="(companion, index) in companionGuests"
                :key="companion.id"
                class="checkout-summary__item-row"
              >
                <div class="checkout-summary__item-number">{{ index + 2 }}</div>
                <div class="checkout-summary__item-content">
                  <p class="checkout-summary__item-title">
                    {{
                      $t('landingpage.checkout-page.confirmation.companion', {
                        num: index + 1,
                      })
                    }}
                  </p>
                  <p
                    v-if="companion?.contact.first_name"
                    class="checkout-summary__item-subtitle"
                  >
                    {{ companion?.contact.first_name ?? '' }}
                    {{ companion?.contact.last_name ?? '' }}
                  </p>

                  <!-- Companion Guest Ticket -->
                  <div
                    v-for="item in companion?.items.filter(
                      (item) => item.product.type === 'ticket',
                    )"
                    :key="item.id"
                    class="checkout-summary__item-price-row"
                  >
                    <p class="checkout-summary__item-label">
                      {{ item?.product?.name[activeLocale] }}
                    </p>
                    <p v-if="!isFree" class="checkout-summary__item-price">
                      {{ getTicketProductPrice(item)?.formatted }}
                    </p>
                  </div>

                  <!-- Companion Guest Add Ons -->
                  <template
                    v-for="(group, groupIndex) in groupAddonItems(
                      companion?.items.filter(
                        (item) => item.product.type === 'addon',
                      ) ?? [],
                    )"
                    :key="`${group.product.id}-${groupIndex}`"
                  >
                    <div class="checkout-summary__item-price-row add-on">
                      <p class="checkout-summary__item-label">
                        +
                        {{
                          getAvailabilityType(group.items[0]) === 'fixed'
                            ? group.items[0].quantity > 1
                              ? `${group.items[0].quantity}x`
                              : ''
                            : group.items.length > 1
                              ? `${group.items.length}x`
                              : ''
                        }}
                        {{ group.product?.name[activeLocale] }}
                      </p>
                      <p
                        v-if="
                          !isFree &&
                          getAvailabilityType(group.items[0]) === 'fixed'
                        "
                        class="checkout-summary__item-price"
                      >
                        {{ getAddOnProductPrice(group.items[0]) }}
                      </p>
                    </div>

                    <template
                      v-if="getAvailabilityType(group.items[0]) === 'flexible'"
                    >
                      <div
                        v-for="dateGroup in groupItemsByDate(group.items)"
                        :key="dateGroup.date"
                        class="add-on-date"
                      >
                        <p class="text-[10px]! font-medium">
                          {{ dateGroup.date }}
                        </p>
                        <div
                          v-for="item in dateGroup.slots"
                          :key="item.id"
                          class="flex justify-between"
                        >
                          <p class="text-[10px]!">
                            {{
                              `${formatTime(item?.starts_at, eventSettings?.timezone, false)} - ${formatTime(item?.ends_at, eventSettings?.timezone, false)} (${Math.round((new Date(item?.ends_at) - new Date(item?.starts_at)) / 60000)} min)`
                            }}
                          </p>
                          <p class="text-[10px]!">
                            {{ getAddOnProductPrice(item) }}
                          </p>
                        </div>
                      </div>
                    </template>

                    <div
                      v-else-if="
                        getAvailabilityType(group.items[0]) === 'nights'
                      "
                      class="add-on-date"
                    >
                      <template
                        v-for="range in [getNightsDateRange(group.items)]"
                        :key="range"
                      >
                        <p class="text-[10px]!">
                          {{ formatDateWithWeekday(range.first?.starts_at) }}
                          -
                          {{ formatDateWithWeekday(range.last?.ends_at) }}
                          ({{ group.items.length }}
                          {{
                            group.items.length === 1
                              ? $t('common.night')
                              : $t('common.nights')
                          }})
                        </p>
                      </template>

                      <p
                        v-for="(breakdown, idx) in nightsCalculationBreakdown(
                          group.items,
                        )"
                        :key="idx"
                        class="flex gap-2 justify-between mt-1"
                      >
                        <span>
                          {{ breakdown.count }}x
                          {{
                            formatCurrencyLeft(
                              breakdown.grossPrice,
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              },
                              orderDetails?.currency,
                            )
                          }}
                        </span>
                        <span>
                          {{
                            formatCurrencyLeft(
                              breakdown.count * breakdown.grossPrice,
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              },
                              orderDetails?.currency,
                            )
                          }}
                        </span>
                      </p>
                    </div>
                  </template>
                </div>
              </div>
            </template>
          </template>
        </div>
      </template>

      <div
        class="checkout-summary__totals"
        :class="[
          isFree && !orderDetails?.promo_code && 'is-free',
          !mainGuestTickets.length &&
            !mainGuestAddOns.length &&
            !groupedTickets.length &&
            'no-selected',
        ]"
      >
        <div
          v-if="orderDetails?.promo_discount > 0"
          class="checkout-summary__totals-row subtotal"
          :class="isMobileItemsShown && 'mobile-show'"
        >
          <p class="checkout-summary__totals-label">
            {{ $t('landingpage.checkout-page.summary.totals.subtotal') }}
          </p>
          <p class="checkout-summary__totals-value">
            {{
              formatCurrencyLeft(
                (orderDetails?.total_gross_price || 0) +
                  (orderDetails?.promo_discount || 0),
                { minimumFractionDigits: 2, maximumFractionDigits: 2 },
                orderDetails?.currency,
              )
            }}
          </p>
        </div>

        <div
          v-if="orderDetails?.promo_discount > 0"
          class="checkout-summary__totals-row discount"
          :class="isMobileItemsShown && 'mobile-show'"
        >
          <div>
            <p class="checkout-summary__totals-label">
              {{ $t('landingpage.checkout-page.summary.totals.discount') }}
            </p>
            <p
              v-if="orderDetails?.promo_code"
              class="checkout-summary__totals-promo-code"
            >
              {{ orderDetails.promo_code.code }}
              ({{
                orderDetails.promo_code.discount_type === 'percentage'
                  ? `${orderDetails.promo_code.discount_value}%`
                  : formatCurrencyLeft(
                      parseFloat(orderDetails.promo_code.discount_value),
                      { minimumFractionDigits: 2, maximumFractionDigits: 2 },
                      orderDetails?.currency,
                    )
              }})
              <button
                type="button"
                class="checkout-promo-code__remove-btn"
                :disabled="isRemovingPromoCode"
                @click="removePromoCode"
              >
                <FontAwesomeIcon :icon="['far', 'xmark']" />
              </button>
            </p>
          </div>
          <p class="checkout-summary__totals-value">
            -{{
              formatCurrencyLeft(
                orderDetails?.promo_discount || 0,
                { minimumFractionDigits: 2, maximumFractionDigits: 2 },
                orderDetails?.currency,
              )
            }}
          </p>
        </div>

        <div
          class="checkout-summary__totals-row checkout-summary__totals-row--grand"
        >
          <p class="checkout-summary__totals-grand-label">
            <span
              v-if="!isFree || orderDetails?.promo_code"
              class="hidden lg:block"
            >
              {{ $t('landingpage.checkout-page.summary.totals.grand_total') }}
            </span>

            <span class="block lg:hidden">
              <button
                class="flex gap-2 items-center"
                @click="toggleMobileSummary()"
              >
                <FontAwesomeIcon
                  :icon="['far', 'cart-shopping']"
                  class="text-sm"
                />
                {{
                  $t('landingpage.checkout-page.summary.totals.items', {
                    count: orderDetails?.line_items?.length || 0,
                  })
                }}
                <FontAwesomeIcon
                  :icon="[
                    'far',
                    isMobileItemsShown ? 'chevron-down' : 'chevron-up',
                  ]"
                  class="text-sm"
                />
              </button>
            </span>
          </p>
          <p
            v-if="!isFree || orderDetails?.promo_code"
            class="checkout-summary__totals-grand-value"
          >
            {{
              formatCurrencyLeft(
                orderDetails?.total_gross_price || 0,
                { minimumFractionDigits: 2, maximumFractionDigits: 2 },
                orderDetails?.currency,
              )
            }}
          </p>
        </div>

        <div
          class="checkout-summary__totals-row vat"
          :class="isMobileItemsShown && 'mobile-show'"
        >
          <p class="checkout-summary__totals-label">
            {{ $t('landingpage.checkout-page.summary.totals.vat') }}
          </p>
          <p class="checkout-summary__totals-value">
            {{
              formatCurrencyLeft(
                orderDetails?.total_vat || 0,
                { minimumFractionDigits: 2, maximumFractionDigits: 2 },
                orderDetails?.currency,
              )
            }}
          </p>
        </div>
      </div>

      <PromoCode
        v-if="
          !isFree &&
          activeStep !== 'confirmation' &&
          orderDetails?.has_promo_codes &&
          !orderDetails?.promo_code
        "
        :class="isMobileItemsShown && 'mobile-show'"
        @applied="emit('promo-code-changed')"
        @removed="emit('promo-code-changed')"
      />

      <div class="checkout-summary__footer">
        <div v-if="isOrderStep" class="checkout-summary__terms-conditions">
          <!-- eslint-disable vue/no-v-html -->
          <p
            class="[&>a]:underline"
            v-html="
              $t('landingpage.checkout-page.summary.terms_conditions', {
                company: eventSettings?.billing_company_name || '',
                terms_of_use_url: eventSettings?.terms_of_use_url || '',
                privacy_policy_url: eventSettings?.privacy_policy_url || '',
              })
            "
          />
        </div>

        <button
          type="button"
          class="checkout-summary__btn-next btn btn-primary"
          :class="disableNext && 'btn-disabled'"
          :disabled="disableNext"
          @click="emit('next')"
        >
          {{ getNextBtnLabel }}
        </button>

        <p
          v-if="activeStep === 'payment' && paymentHint"
          class="checkout-summary__payment-hint"
        >
          {{ paymentHint }}
        </p>

        <button
          v-if="showBackButton"
          type="button"
          class="checkout-summary__btn-back btn btn-outline-primary"
          @click="emit('back')"
        >
          <FontAwesomeIcon :icon="['far', 'chevron-left']" />
          {{ $t('common.back') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { inject, ref, computed, onMounted } from 'vue'
import { useFetch } from '@/LandingPage/Checkout/useCheckoutFetch'
import { intlFormat } from 'date-fns'
import { formatCurrencyLeft } from '@/composables/currency'
import { formatDate, formatTime } from '@/composables/date'
import { useCurrentLocaleCode } from '@/composables/locale'
import { useI18n } from 'vue-i18n'
import CountdownTimer from '@/Components/LandingPage/Checkout/CountdownTimer.vue'
import PromoCode from '@/Components/LandingPage/Checkout/PromoCode.vue'

const { t } = useI18n()

defineProps({
  disableNext: { type: Boolean, default: () => false },
})

const emit = defineEmits([
  'next',
  'back',
  'countdown-timer-expired',
  'promo-code-changed',
])

const eventId = inject('eventId')
const orderId = inject('orderId')
const route = window.route
const isRemovingPromoCode = ref(false)

const removePromoCode = async () => {
  if (isRemovingPromoCode.value) return

  isRemovingPromoCode.value = true

  const { statusCode } = await useFetch(
    route('api.checkout.promo-code.remove', {
      event: eventId,
      order: orderId.value,
    }),
  ).delete()

  isRemovingPromoCode.value = false

  if (statusCode.value === 204 || statusCode.value === 200) {
    emit('promo-code-changed')
  }
}

const mainGuestTickets = computed(() => {
  return (
    mainGuest.value?.items?.filter((item) => item.product.type === 'ticket') ??
    []
  )
})
const mainGuestAddOns = computed(() => {
  return (
    mainGuest.value?.items?.filter((item) => item.product.type === 'addon') ??
    []
  )
})

const addonTypePriority = { nights: 0, flexible: 1, fixed: 2 }

const groupAddonItems = (items) => {
  const groups = []
  const flexibleMap = {}
  const nightsMap = {}

  for (const item of items) {
    const type = getAvailabilityType(item)
    if (type === 'flexible') {
      const productId = item.product.id
      if (!flexibleMap[productId]) {
        flexibleMap[productId] = { product: item.product, items: [] }
        groups.push(flexibleMap[productId])
      }
      flexibleMap[productId].items.push(item)
    } else if (type === 'nights') {
      const productId = item.product.id
      if (!nightsMap[productId]) {
        nightsMap[productId] = { product: item.product, items: [] }
        groups.push(nightsMap[productId])
      }
      nightsMap[productId].items.push(item)
    } else {
      groups.push({ product: item.product, items: [item] })
    }
  }

  return groups.sort((a, b) => {
    const typeA = getAvailabilityType(a.items[0]) ?? 'fixed'
    const typeB = getAvailabilityType(b.items[0]) ?? 'fixed'
    return (addonTypePriority[typeA] ?? 2) - (addonTypePriority[typeB] ?? 2)
  })
}

const groupedMainGuestAddOns = computed(() =>
  groupAddonItems(mainGuestAddOns.value),
)
const showBackButton = computed(() => {
  return activeStepIndex.value > 0
})
const getNextBtnLabel = computed(() => {
  if (
    isFree.value &&
    checkoutSteps.value[activeStepIndex.value + 1] === 'confirmation'
  ) {
    return t('landingpage.checkout-page.complete-registration.btn-label')
  }

  if (
    orderType.value === 'add-ons' &&
    checkoutSteps.value[activeStepIndex.value + 1] === 'select-ticket'
  ) {
    return t('landingpage.checkout-page.continue-to-select-ticket.btn-label')
  }

  if (checkoutSteps.value[activeStepIndex.value + 1] === 'companions') {
    return t('landingpage.checkout-page.continue-to-companions.btn-label')
  }

  return t(
    `landingpage.checkout-page.continue-to-${checkoutSteps.value[activeStepIndex.value + 1]}.btn-label`,
  )
})

// The order-placing step (Terms & the "Order" button live here): the last step before confirmation,
// i.e. billing for card-only merchants or the payment step when one is offered.
const isOrderStep = computed(
  () => checkoutSteps.value[activeStepIndex.value + 1] === 'confirmation',
)

const paymentHint = computed(() => {
  if (selectedPaymentMethod.value === 'bank_transfer') {
    return t('landingpage.checkout-page.summary.payment-hint.bank-transfer')
  }
  if (selectedPaymentMethod.value === 'online') {
    return t('landingpage.checkout-page.summary.payment-hint.online')
  }
  return ''
})

const orderDetails = inject('orderDetails')
const isCompanionsEnabled = inject('isCompanionsEnabled')
const mainGuest = inject('mainGuest')
const companionGuests = inject('companionGuests')
const tickets = inject('tickets')
const orderType = inject('orderType')
const isFree = inject('isFree')
const checkoutSteps = inject('checkoutSteps')
const activeStep = inject('activeStep')
const activeStepIndex = inject('activeStepIndex')
const selectedPaymentMethod = inject('selectedPaymentMethod', ref(null))
const activeLocale = inject('activeLocale')
const eventSettings = inject('eventSettings')
const addOns = inject('addOns')
const countdownTimerRef = ref(null)

const isTicketPurchaseMode = computed(() => {
  return orderDetails.value?.checkout_mode === 'ticket_purchase'
})

const groupedTickets = computed(() => {
  if (!isTicketPurchaseMode.value) return []

  const ticketGroups = {}
  orderDetails.value?.line_items?.forEach((lineItem) => {
    if (lineItem.product_type?.includes('TicketBookable')) {
      const groupKey = lineItem.bookable_id ?? lineItem.name[activeLocale]
      if (!ticketGroups[groupKey]) {
        ticketGroups[groupKey] = {
          name: lineItem.name[activeLocale],
          quantity: 0,
          unitPrice: lineItem.unit_gross_price,
          totalPrice: 0,
        }
      }
      ticketGroups[groupKey].quantity += lineItem.quantity
      ticketGroups[groupKey].totalPrice += lineItem.total_gross_price
    }
  })

  return Object.values(ticketGroups)
})

const isMobileItemsShown = ref(false)

onMounted(() => {
  if (orderDetails.value?.line_items?.length) {
    startCountdown()
  }
})

const getAddOnProductPrice = (item) => {
  const addonLineItem = orderDetails.value?.line_items.find(
    ({ bookable_reservations, product_type }) => {
      return (
        product_type.includes('UniversalBookable') &&
        bookable_reservations?.some((reservation) => reservation.id === item.id)
      )
    },
  )

  return formatCurrencyLeft(
    addonLineItem?.total_gross_price || 0,
    { minimumFractionDigits: 2, maximumFractionDigits: 2 },
    orderDetails.value?.currency,
  )
}

const getTicketProductPrice = (item) => {
  const ticket =
    tickets.value?.find(({ id }) => id === item?.product?.id) || null
  return {
    price: ticket?.price || 0,
    formatted: formatCurrencyLeft(
      ticket?.price || 0,
      { minimumFractionDigits: 2, maximumFractionDigits: 2 },
      orderDetails.value?.currency,
    ),
  }
}

const toggleMobileSummary = () =>
  (isMobileItemsShown.value = !isMobileItemsShown.value)

const groupItemsByDate = (items) => {
  const map = {}
  for (const item of items) {
    const dateLabel = formatDate(item?.starts_at, eventSettings?.timezone)
    if (!map[dateLabel]) {
      map[dateLabel] = []
    }
    map[dateLabel].push(item)
  }
  return Object.entries(map).map(([date, slots]) => ({ date, slots }))
}

const getAvailabilityType = (item) => {
  const addOn = addOns.value
    ?.flatMap((group) => group.addons)
    .find((addon) => addon.id === item?.product?.id)
  return addOn?.availability_type
}

const formatDateWithWeekday = (date) => {
  return intlFormat(
    new Date(date),
    {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    },
    { locale: useCurrentLocaleCode() },
  )
}

const getNightsSortedItems = (items) => {
  return [...items].sort(
    (a, b) => new Date(a.starts_at) - new Date(b.starts_at),
  )
}

const getNightsDateRange = (items) => {
  if (!items.length) {
    return { first: undefined, last: undefined }
  }
  const sorted = getNightsSortedItems(items)
  return { first: sorted[0], last: sorted[sorted.length - 1] }
}

const nightsCalculationBreakdown = (items) => {
  const groups = []

  for (const item of items) {
    const lineItem = orderDetails.value?.line_items?.find(
      ({ bookable_reservations, product_type }) => {
        return (
          product_type.includes('UniversalBookable') &&
          bookable_reservations?.some(
            (reservation) => reservation.id === item.id,
          )
        )
      },
    )

    const gross = lineItem?.total_gross_price ?? 0
    const existing = groups.find((g) => g.grossPrice === gross)
    if (existing) {
      existing.count++
    } else {
      groups.push({ count: 1, grossPrice: gross })
    }
  }

  return groups
}

const startCountdown = () => {
  countdownTimerRef.value?.initCountdown()
}

defineExpose({
  startCountdown,
})
</script>
